// libs
import React, { memo, useState, useRef, useEffect } from 'react';

// styles
import {
  SizeChartContainer,
  BoxTitleContainer,
  BoxTableContainer,
  Title,
  SizeTable,
  SizeTableData,
  SizeTableHeading,
  BgImage,
  HeadingChild,
  StyledIOElement,
} from './styles';

//components
import ToggleButton from './ToggleButton';
import EmptyStateImage from './EmptyStateImage';

//constants
import {
  TABLE_TITLE,
  IO_OPTIONS,
  ANIMATION_TIME,
  SVG_SIZE,
  PAGE_PADDING,
  SCROLL_BUFFER,
} from './constants';

//helpers
import NFA from 'shared/helpers/analytics/NFAnalytics';

interface SizeDataArray {
  conversion: boolean;
  id: string;
  label: string;
  sizeName: string;
  type: string;
  value: string;
}
interface SelectedSizeChartProps {
  sizeData: {
    [key: string]: { [key: string]: SizeDataArray };
  };
  sizeName: string;
  isOutOfStock: boolean;
  showToggle: boolean;
  isBottomSheet?: boolean;
}

const SelectedSizeChart = ({
  sizeData,
  sizeName,
  showToggle,
  isBottomSheet,
}: SelectedSizeChartProps) => {
  const units = Object.keys(sizeData);
  const [isInteracted, setIsInteracted] = useState(false);
  const [currentUnit, setCurrentUnit] = useState(units[0]);
  const [showAnimation, setShowAnimation] = useState(false);
  const lastColumnRef = useRef(null);
  const sizeChartRef = useRef(null);
  const dataToBeRendered = Object.values(sizeData[currentUnit]);
  const [lastColumnPosition, setLastColumnPosition] = useState(0);
  const [showEmptyStateImage, setShowEmptyStateImage] = useState(false);

  const onToggleClicked = (value: string) => {
    setCurrentUnit(value);
    if (!isInteracted) {
      NFA.updateDataLayer({
        pdpChartInteraction: true,
      });
      setIsInteracted(true);
    }
  };

  useEffect(() => {
    const { current: lastColumnElement } = lastColumnRef;
    const { current: sizeChartElement } = sizeChartRef;

    if (lastColumnElement && sizeChartElement) {
      const sizeChartWidth = sizeChartElement.getBoundingClientRect().width;
      const { left: columnLeft, width: columnWidth } = lastColumnElement.getBoundingClientRect();
      const columnPosition = columnLeft - PAGE_PADDING;

      let newLastColumnPosition = 0;
      if (columnPosition + columnWidth >= sizeChartWidth) {
        newLastColumnPosition = Math.min(
          sizeChartWidth / 2,
          columnPosition - sizeChartWidth + columnWidth + SCROLL_BUFFER
        );
      }
      const isEmptystate = sizeChartWidth - (columnPosition + columnWidth) > SVG_SIZE;
      setLastColumnPosition(newLastColumnPosition);
      setShowEmptyStateImage(isEmptystate);
    }
  }, []);

  return (
    <StyledIOElement
      onVisible={() =>
        setTimeout(() => {
          setShowAnimation(true);
        }, ANIMATION_TIME)
      }
      onHidden={() => setShowAnimation(false)}
      options={IO_OPTIONS}
      once
      className={isBottomSheet ? 'bottomsheet-wrap' : ''}
    >
      <SizeChartContainer ref={sizeChartRef}>
        <BoxTitleContainer>
          <Title>
            {TABLE_TITLE} <b>{sizeName}</b>
          </Title>

          {showToggle && <ToggleButton values={units} onChange={onToggleClicked} />}
        </BoxTitleContainer>
        <BoxTableContainer>
          {showEmptyStateImage && (
            <BgImage>
              <EmptyStateImage />
            </BgImage>
          )}
          <SizeTable
            className={showAnimation ? 'show-animation' : ''}
            lasColumnPosition={lastColumnPosition}
            singleColumn={dataToBeRendered.length === 1}
          >
            <tr>
              {dataToBeRendered?.map((data, i) => {
                return (
                  <SizeTableHeading
                    key={i}
                    className="heading"
                    isSetWidth={dataToBeRendered.length === 3 && i === 2}
                  >
                    <HeadingChild>{data.label}</HeadingChild>
                  </SizeTableHeading>
                );
              })}
            </tr>
            <tr>
              {dataToBeRendered?.map((data, i) => {
                return (
                  <SizeTableData
                    key={i}
                    className="data"
                    ref={i === dataToBeRendered.length - 1 ? lastColumnRef : null}
                  >
                    <div>{data.value}</div>
                  </SizeTableData>
                );
              })}
            </tr>
          </SizeTable>
        </BoxTableContainer>
      </SizeChartContainer>
    </StyledIOElement>
  );
};

export default memo(SelectedSizeChart);
